import { Controller } from '@hotwired/stimulus'
import { disable, enable } from '../../../helpers/custom_events_helper'
import { GRID_EVENTS, RESOLUTIONS } from './grid_events'

const WHOLE_UNITS = /^\d+$/
const PARTIAL_UNITS = /^\d+(\.\d{1,2})?$/
const roundUnits = n => Math.round(n * 100) / 100

// The "Decision needed for this cell" modal: pick a choice (editing its value if needed) and confirm to
// settle the uploaded value; each choice's day total updates as you type.
export default class extends Controller {
  static targets = ['error']
  static values = { resolvePath: String, committed: Number, partialUnits: Boolean, maxUnits: Number }

  connect() {
    this.element.querySelectorAll('.grid-decision-modal__option').forEach(option => this.recompute(option))
    this.syncTabStops()
  }

  // Choosing an option (arrow keys on the radios, or clicking the option or its value field).
  select(event) {
    const radio = event.target.closest('.grid-decision-modal__option')?.querySelector('input[name="choice"]')
    if (!radio || radio.disabled) return
    radio.checked = true
    this.syncTabStops()
    this.updateConfirm()
  }

  // Tab goes radios → the chosen option's value field → Confirm. Other options' fields stay out of the
  // tab order, so tabbing past them can't change the choice; arrow keys move between options.
  syncTabStops() {
    this.element.querySelectorAll('.grid-decision-modal__option').forEach(option => {
      const field = option.querySelector('[data-decision-value]')
      if (field) field.tabIndex = option.querySelector('input[name="choice"]')?.checked ? 0 : -1
    })
  }

  // Typing a value makes its option the choice.
  editValue(event) {
    const field = event.target
    const option = field.closest('.grid-decision-modal__option')
    option.querySelector('input[name="choice"]').checked = true
    this.syncTabStops()
    this.recompute(option)
    this.updateConfirm()
  }

  recompute(option) {
    const field = option.querySelector('[data-decision-value]')
    const totalEl = option.querySelector('[data-decision-total]')
    if (!field || !totalEl) return

    if (!this.validValue(field.value)) {
      totalEl.textContent = '—'
      return
    }

    const n = Number(field.value.trim())
    totalEl.textContent = roundUnits(
      field.dataset.mode === 'add' ? this.committedValue + n : this.committedValue - Number(field.dataset.old) + n
    )
  }

  validValue(value) {
    return !this.unitsError(value)
  }

  // Mirrors TempServiceRecord.invalid_reason_for.
  unitsError(value) {
    const text = value.trim()
    if (text === '') return 'Enter the units to apply.'
    const number = Number(text)
    if (Number.isNaN(number)) return `'${text}' isn't a number.`
    if (number < 0) return `${text} can't be negative.`
    if (this.hasMaxUnitsValue && number > this.maxUnitsValue)
      return `${text} is above the maximum of ${this.maxUnitsValue}.`
    if (!this.partialUnitsValue && !WHOLE_UNITS.test(text)) {
      return `${text} isn't a whole number, and this service is reported in whole units.`
    }
    if (!PARTIAL_UNITS.test(text)) return `${text} isn't a valid number of units.`
    return null
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message || ''
    this.errorTarget.classList.toggle('d-none', !message)
  }

  selectedOption() {
    const radio = this.element.querySelector('input[name="choice"]:checked')
    return radio ? radio.closest('.grid-decision-modal__option') : null
  }

  // Confirm sits in the modal footer, outside this element, and submits this form via its form attribute.
  get confirmButton() {
    return document.querySelector(`button[form="${this.element.id}"]`)
  }

  updateConfirm() {
    const button = this.confirmButton
    if (!button) return
    const option = this.selectedOption()
    const field = option?.querySelector('[data-decision-value]')
    const error = field && field.value.trim() !== '' ? this.unitsError(field.value) : null
    this.showError(error)
    const ok = !!option && (!field || this.validValue(field.value))
    // Confirm renders disabled; Galette's button controller keeps its look in step with these.
    if (ok) enable(button)
    else disable(button)
  }

  // Also bound to Enter: Galette enables Confirm a tick late, so an immediate Enter would do nothing.
  async confirm(event) {
    event.preventDefault()
    const option = this.selectedOption()
    if (!option || !this.hasResolvePathValue || this.confirming) return

    const radio = option.querySelector('input[name="choice"]')
    const choice = radio.value
    const serviceRecordId = radio.dataset.serviceRecordId
    const field = option.querySelector('[data-decision-value]')
    if (field && !this.validValue(field.value)) return
    const value = field ? Number(field.value.trim()) : null
    const totalEl = option.querySelector('[data-decision-total]')
    const dayTotal = totalEl ? Number(totalEl.textContent) : this.committedValue

    const headers = { 'Content-Type': 'application/json' }
    const csrfToken = document.querySelector('[name="csrf-token"]')
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken.content

    this.confirming = true
    try {
      const body = { choice }
      if (serviceRecordId) body.service_record_id = serviceRecordId
      if (value != null) body.value = value
      const response = await fetch(this.resolvePathValue, { method: 'PATCH', headers, body: JSON.stringify(body) })
      // A toast would sit under the open dialog.
      if (response.status === 422) {
        const { error } = await response.json()
        this.showError(error)
        return
      }
      if (!response.ok) throw new Error(`Unexpected response ${response.status}`)

      document.dispatchEvent(
        new CustomEvent(GRID_EVENTS.decisionConfirmed, {
          detail: { choice, dayTotal, resolution: this.resolutionFor(choice) }
        })
      )
      this.close()
    } catch (error) {
      console.error('Error resolving decision:', error)
      this.showError('Failed to apply the decision. Try again.')
    } finally {
      this.confirming = false
    }
  }

  // The uploaded value ends up as an update (place / add), a delete, or a skip.
  resolutionFor(choice) {
    if (choice === 'skip') return RESOLUTIONS.skipped
    if (choice === 'delete') return RESOLUTIONS.delete
    return RESOLUTIONS.update
  }

  close() {
    this.element.closest('dialog')?.close()
  }
}
